import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { projectsAPI, settingsAPI } from '../api';

function calcDuration(start, end) {
  if (!start) return '—';
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const days = Math.floor((e - s) / (1000 * 60 * 60 * 24));
  if (days < 0) return '—';
  const months = Math.floor(days / 30);
  const remDays = days % 30;
  if (months === 0) return `${days} days`;
  if (remDays === 0) return `${months} month${months>1?'s':''}`;
  return `${months} month${months>1?'s':''} ${remDays} day${remDays>1?'s':''}`;
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [depts, setDepts] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('tile');
  const [form, setForm] = useState({ name: '', description: '', department: '', status: 'active', start_date: '', end_date: '' });
  const navigate = useNavigate();

  const load = () => {
    projectsAPI.getAll().then(r => setProjects(r.data));
    settingsAPI.getType('department').then(r => setDepts(r.data));
    settingsAPI.getType('project_status').then(r => setProjectStatuses(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) return toast.error('Project name required');
    try {
      await projectsAPI.create(form);
      toast.success('Project created');
      setShowModal(false);
      setForm({ name: '', description: '', department: '', status: 'active', start_date: '', end_date: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Projects</div>
          <div className="page-subtitle">{projects.length} NPD projects</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Project</button>
      </div>

      {/* Project Stats */}
      {projects.length > 0 && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card purple">
            <div className="stat-label">Total Projects</div>
            <div className="stat-value">{projects.length}</div>
          </div>
          <div className="stat-card blue">
            <div className="stat-label">Total Products</div>
            <div className="stat-value">{projects.reduce((s, p) => s + (p.product_count || 0), 0)}</div>
          </div>
          <div className="stat-card amber">
            <div className="stat-label">Total R&D Cost</div>
            <div className="stat-value" style={{ fontSize: 18 }}>AED {projects.reduce((s, p) => s + (p.total_cost || 0), 0).toLocaleString('en-AE', { maximumFractionDigits: 0 })}</div>
          </div>
          <div className="stat-card purple">
            <div className="stat-label">Active Projects</div>
            <div className="stat-value">{projects.filter(p => p.status === 'active').length}</div>
          </div>
        </div>
      )}

      <div className="card">
        {projects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">◈</div>
            <p>No projects yet. Create your first NPD project.</p>
          </div>
        ) : (
          <>
            {/* Search and Filter */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
              <input
                placeholder="Search projects..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                {['all','active','completed','closed'].map(s => (
                  <button key={s} onClick={() => setStatusFilter(s)}
                    className={`btn btn-sm ${statusFilter===s ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: 11, textTransform: 'capitalize' }}>{s}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setViewMode('tile')} className={`btn btn-sm ${viewMode==='tile' ? 'btn-primary' : 'btn-secondary'}`} title="Tile view">⊞</button>
                <button onClick={() => setViewMode('list')} className={`btn btn-sm ${viewMode==='list' ? 'btn-primary' : 'btn-secondary'}`} title="List view">☰</button>
              </div>
            </div>
            {/* Project Cards / List */}
            {(() => {
              const filtered = projects
                .filter(p => statusFilter === 'all' || p.status === statusFilter)
                .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.project_code.toLowerCase().includes(search.toLowerCase()));

              if (viewMode === 'list') return (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Code</th><th>Name</th><th>Dept</th><th style={{textAlign:'center'}}>FG</th><th style={{textAlign:'center'}}>SFG</th><th>Status</th><th>Duration</th><th className="text-right">R&D Cost</th></tr>
                    </thead>
                    <tbody>
                      {filtered.map(p => (
                        <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} style={{ cursor: 'pointer' }}>
                          <td className="mono" style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 11 }}>{p.project_code}</td>
                          <td style={{ fontWeight: 600 }}>{p.name}</td>
                          <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{p.department || '—'}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--accent)' }}>{p.fg_count || 0}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--text-muted)' }}>{p.sfg_count || 0}</td>
                          <td><span className={`badge badge-${p.status}`}>{p.status}</span></td>
                          <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{calcDuration(p.start_date, p.end_date)}</td>
                          <td className="text-right cost-value" style={{ fontWeight: 700 }}>AED {(p.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 0 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                  {filtered.map(p => (
                    <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700 }}>{p.project_code}</span>
                        <span className={`badge badge-${p.status}`}>{p.status}</span>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 3, lineHeight: 1.25 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>{p.department || '—'} · {calcDuration(p.start_date, p.end_date)}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{p.fg_count || 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>FG</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)' }}>{p.sfg_count || 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>SFG</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber)' }}>AED {(p.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 0 })}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>R&D Cost</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">New NPD Project</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Project Name *</label>
                <input placeholder="e.g. New Snack Range" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Code will be auto-generated as <strong style={{ color: 'var(--accent)', fontFamily: 'monospace' }}>NPD-{new Date().getFullYear().toString().slice(-2)}-####</strong></div>
              </div>
              <div className="form-group">
                <label>Department</label>
                <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                  <option value="">Select department</option>
                  {depts.map(d => <option key={d.id}>{d.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {projectStatuses.map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>End Date (Target)</label>
                <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </div>
              <div className="form-group full">
                <label>Description</label>
                <textarea placeholder="Project description..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submit}>Create Project</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
